import { useCallback, useEffect, useRef, useState } from "react";

export const mediaRecorderErrors = {
  RECORDER_API_NOT_SUPPORTED_IN_BROWSER: "RECORDER_API_NOT_SUPPORTED_IN_BROWSER",
  NO_MEDIA_STREAM_AVAILABLE: "NO_MEDIA_STREAM_AVAILABLE",
  INVALID_MIME_TYPE_SUPPORT: "INVALID_MIME_TYPE_SUPPORT",
  RECORDING_FAILED: "RECORDING_FAILED",
} as const;

export type MediaRecorderErrors = (typeof mediaRecorderErrors)[keyof typeof mediaRecorderErrors];
export interface UseMediaRecorderProps {
  mediaStream: MediaStream | null;
  onError?: (error: string) => void;
  mimeType?: string;
}

export const useMediaRecorder = ({ mediaStream, onError, mimeType: mimeTypeParam }: UseMediaRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);
  const [recordedFileUrl, setRecordedFileUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeType = useRef<string>("video/webm");

  const videoBitsPerSecond = 1 * 1024 * 1024;

  const handleError = useCallback(
    (errorType: MediaRecorderErrors) => {
      const errorMessage = mediaRecorderErrors[errorType];
      setError(errorMessage);
      onError?.(errorMessage);
    },
    [onError],
  );

  // Initialize media recorder when mediaStream changes
  useEffect(() => {
    if (!mediaStream) {
      handleError(mediaRecorderErrors.NO_MEDIA_STREAM_AVAILABLE);
      return;
    }

    if (!window.MediaRecorder) {
      handleError(mediaRecorderErrors.RECORDER_API_NOT_SUPPORTED_IN_BROWSER);
      return;
    }

    try {
      // check support
      if (mimeTypeParam && MediaRecorder.isTypeSupported(mimeTypeParam)) {
        mimeType.current = mimeTypeParam;
      } else {
        mimeType.current = "video/webm";
      }

      const recorder: MediaRecorder = new MediaRecorder(mediaStream, {
        mimeType: mimeType.current,
        videoBitsPerSecond,
      });

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType.current });
        const videoType = mimeType.current.split(";")[0].trim();
        const videoFormate = videoType.split("/")[1];
        setRecordedFile(new File([blob], `video.${videoFormate}`, { type: videoType }));
        setRecordedFileUrl(URL.createObjectURL(blob));
        chunksRef.current = [];
      };

      recorder.onerror = () => {
        handleError(mediaRecorderErrors.RECORDING_FAILED);
      };
    } catch {
      handleError(mediaRecorderErrors.RECORDING_FAILED);
    }
  }, [mediaStream, handleError, videoBitsPerSecond, mimeTypeParam]);

  const startRecording = useCallback(() => {
    if (!mediaRecorderRef.current) {
      handleError(mediaRecorderErrors.NO_MEDIA_STREAM_AVAILABLE);
      return;
    }

    if (mediaRecorderRef.current.state === "inactive") {
      chunksRef.current = [];
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsPaused(false);
    }
  }, [handleError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
    }
  }, []);

  const DownloadRecordingFile = useCallback(
    (filename: string) => {
      if (recordedFileUrl) {
        const a = document.createElement("a");
        a.href = recordedFileUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(recordedFileUrl);
      }
    },
    [recordedFileUrl],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    },
    [],
  );

  return {
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    DownloadRecordingFile,
    recordedFile,
    recordedFileUrl,
    isRecording,
    isPaused,
    error,
  };
};
